import { get } from "../../api/axios"
import endpoints from "../../api/endpoints"
import { toastApiError } from "../../helpers/apiError"
import { formatDate } from "../../helpers/formatDate"
import { getUserAvatarUrl, resolveMediaUrl } from "../../helpers/userAvatar"
import { getUserFullName } from "../../helpers/userDisplay"
import { mapPhotographyDetailMedias } from "./cityPhotography"

export const REVIEW_TITLE_MAX = 150
export const REVIEW_DESCRIPTION_MAX = 1000
export const REVIEW_MAX_MEDIAS = 3
export const REVIEW_MAX_MEDIA_BYTES = 5 * 1024 * 1024
export const REVIEW_MEDIA_ACCEPT = "image/png,image/jpeg,image/jpg,image/webp"
export const REVIEW_ACCEPTED_TYPES = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
]
export const REVIEW_DEFAULT_STATUS = "published"

const IMAGE_EXTENSIONS = /\.(jpe?g|png|webp)$/i

const sameId = (a, b) => a != null && b != null && String(a) === String(b)

const unwrapEntity = payload => {
  if (!payload || typeof payload !== "object") return null
  if (payload.id) return payload
  if (payload.data?.id) return payload.data
  return null
}

const getDisplayName = user => getUserFullName(user)

const resolveMediaId = media => {
  const id =
    media?.id ||
    media?.media_id ||
    media?.mediaId ||
    media?.uuid ||
    media?.media?.id ||
    null
  return id != null && String(id).trim() !== "" ? String(id) : null
}

const appendArray = (formData, key, values = []) => {
  ;(Array.isArray(values) ? values : []).forEach(value => {
    if (value == null || value === "") return
    formData.append(`${key}[]`, value)
  })
}

export const getEmptyReviewForm = () => ({
  title: "",
  description: "",
  previews: [],
  removedMediaIds: [],
  initialMediaIds: [],
  city_id: "",
  status: REVIEW_DEFAULT_STATUS,
})

export const isAcceptedReviewFile = file => {
  if (!file) return false
  const type = String(file.type || "").toLowerCase()
  if (REVIEW_ACCEPTED_TYPES.includes(type)) return true
  return IMAGE_EXTENSIONS.test(String(file.name || ""))
}

export const revokeReviewPreview = item => {
  if (item?.url && String(item.url).startsWith("blob:")) {
    URL.revokeObjectURL(item.url)
  }
}

export const revokeReviewPreviews = (previews = []) => {
  ;(Array.isArray(previews) ? previews : []).forEach(revokeReviewPreview)
}

export const mapFileToReviewPreview = file => ({
  id: `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`,
  mediaId: null,
  url: URL.createObjectURL(file),
  name: file.name,
  mimeType: file.type,
  isExisting: false,
  file,
})

export const mapExistingReviewMedia = media => {
  const mediaId = resolveMediaId(media)
  const url =
    resolveMediaUrl(media?.media_url) ||
    resolveMediaUrl(media?.url) ||
    resolveMediaUrl(media?.path_url) ||
    resolveMediaUrl(media?.thumbnail_path_url) ||
    null
  return {
    id: mediaId,
    mediaId,
    url,
    name: media?.name || "Existing media",
    mimeType: media?.mime_type || "",
    isExisting: true,
    file: null,
  }
}

export const mapReviewToForm = item => {
  const previews = (Array.isArray(item?.medias) ? item.medias : [])
    .map(mapExistingReviewMedia)
    .filter(media => media.mediaId)
    .slice(0, REVIEW_MAX_MEDIAS)
  const initialMediaIds = previews.map(media => media.mediaId)

  return {
    title: String(item?.title || "").slice(0, REVIEW_TITLE_MAX),
    description: String(item?.description || "").slice(
      0,
      REVIEW_DESCRIPTION_MAX,
    ),
    previews,
    removedMediaIds: [],
    initialMediaIds,
    city_id: item?.city_id || item?.city?.id || "",
    status: item?.status || REVIEW_DEFAULT_STATUS,
  }
}

/** List-card mapping for City Hub Local Reviews. */
export const mapApiReview = item => {
  const medias = Array.isArray(item?.medias) ? item.medias : []
  const firstMedia = medias[0]
  const image =
    resolveMediaUrl(firstMedia?.media_url) ||
    resolveMediaUrl(firstMedia?.url) ||
    null
  const user = item?.user || {}

  return {
    id: item?.id,
    title: item?.title || "Untitled review",
    text: item?.description || "",
    image,
    reviewerId: user?.id || item?.user_id || null,
    cityId: item?.city_id || item?.city?.id || null,
    reviewer: {
      name: getDisplayName(user) || "—",
      avatar: getUserAvatarUrl(user),
    },
    date: formatDate(item?.published_at || item?.created_at, {
      format: "MMM D, YYYY",
      fallback: "—",
    }),
    status: item?.status || "",
    medias,
    raw: item,
  }
}

export const canManageReview = (item, currentUser) => {
  if (!item || !currentUser?.id) return false
  const ownerId =
    item.reviewerId ||
    item.raw?.user?.id ||
    item.raw?.user_id ||
    item.reviewer?.id
  return Boolean(ownerId && sameId(ownerId, currentUser.id))
}

/**
 * Build multipart FormData for create/update member review.
 * Edit is sent with HTTP PATCH (no `_method` spoofing).
 */
export const buildCityReviewFormData = ({
  values,
  cityId,
  isEdit = false,
} = {}) => {
  const formData = new FormData()

  formData.append("title", String(values?.title || "").trim())
  formData.append("description", String(values?.description || "").trim())
  formData.append(
    "status",
    String(values?.status || REVIEW_DEFAULT_STATUS).trim() ||
      REVIEW_DEFAULT_STATUS,
  )

  const resolvedCityId = values?.city_id || cityId
  if (resolvedCityId) formData.append("city_id", resolvedCityId)

  const previews = Array.isArray(values?.previews) ? values.previews : []
  const newFiles = previews
    .map(item => item?.file)
    .filter(file => file instanceof File)
    .slice(0, REVIEW_MAX_MEDIAS)

  newFiles.forEach(file => {
    formData.append("medias[]", file)
  })

  if (isEdit) {
    const keptExistingIds = new Set(
      previews
        .filter(item => item?.isExisting && item?.mediaId)
        .map(item => String(item.mediaId)),
    )
    const initialIds = (
      Array.isArray(values?.initialMediaIds) ? values.initialMediaIds : []
    )
      .map(id => String(id || "").trim())
      .filter(Boolean)
    const explicitRemoved = (
      Array.isArray(values?.removedMediaIds) ? values.removedMediaIds : []
    )
      .map(id => String(id || "").trim())
      .filter(Boolean)

    const removedIds = [
      ...explicitRemoved,
      ...initialIds.filter(id => !keptExistingIds.has(id)),
    ]

    appendArray(formData, "remove_media_ids", [...new Set(removedIds)])
  }

  return formData
}

export const fetchCityReviewDetail = async (
  cityId,
  reviewId,
  { signal } = {},
) => {
  if (!cityId || !reviewId) return { ok: false }

  const response = await get(endpoints.getCityReviewById(cityId, reviewId), {
    signal,
  })

  if (signal?.aborted) return { ok: false, aborted: true }
  if (!response) return { ok: false, aborted: Boolean(signal?.aborted) }

  if (!response?.data?.success) {
    return { ok: false, response }
  }

  const item = unwrapEntity(response.data.result)
  if (!item?.id) return { ok: false, response }

  return {
    ok: true,
    item: {
      ...item,
      uploader: item?.user || item?.uploader || null,
      created_at: item?.published_at || item?.created_at || null,
    },
  }
}

/** Guest-safe review detail for public city pages. */
export const fetchPublicCityReviewDetail = async (
  cityId,
  reviewId,
  { signal } = {},
) => {
  if (!cityId || !reviewId) return { ok: false }

  const response = await get(
    endpoints.getPublicCityReviewById(cityId, reviewId),
    { signal, skipErrorToast: true },
  )

  if (signal?.aborted) return { ok: false, aborted: true }
  if (!response) return { ok: false, aborted: Boolean(signal?.aborted) }

  if (!response?.data?.success) {
    return { ok: false, response }
  }

  const item = unwrapEntity(response.data.result)
  if (!item?.id) return { ok: false, response }

  return {
    ok: true,
    item: {
      ...item,
      uploader: item?.user || item?.uploader || null,
      created_at: item?.published_at || item?.created_at || null,
    },
  }
}

export const fetchReviewDetail = async (id, { signal } = {}) => {
  if (!id) return { ok: false }

  const response = await get(endpoints.getReviewById(id), {
    signal,
  })

  if (signal?.aborted) return { ok: false, aborted: true }
  if (!response) return { ok: false, aborted: Boolean(signal?.aborted) }

  if (!response?.data?.success) {
    toastApiError(response, "Failed to load review")
    return { ok: false }
  }

  const item = unwrapEntity(response.data.result)
  if (!item?.id) {
    toastApiError(response, "Failed to load review")
    return { ok: false }
  }

  return {
    ok: true,
    item: {
      ...item,
      uploader: item?.user || item?.uploader || null,
      created_at: item?.published_at || item?.created_at || null,
    },
  }
}

export const mapReviewDetailMedias = mapPhotographyDetailMedias
